import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import {
  TrendingUp, AlertCircle, CheckCircle, Calendar, Users, CreditCard, Clock, Bus
} from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DashboardAdmin = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    clienti_totali: 0,
    abbonamenti_attivi: 0,
    per_gruppo: [],
    abbonamenti_scaduti: 0,
    pagamenti_sospeso: 0,
    linee_attive: 0,
    corse_oggi: 0
  });
  const [abbonamenti, setAbbonamenti] = useState([]);
  const [abbonamentiInScadenza, setAbbonamentiInScadenza] = useState([]);
  const [scadenzaFromCache, setScadenzaFromCache] = useState(false);
  const [scadenzaLastUpdate, setScadenzaLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [andamentoData, setAndamentoData] = useState([]);
  const [controlliData, setControlliData] = useState([]);
  const [avvisi, setAvvisi] = useState([]);

  useEffect(() => {
    caricaDati();
  }, []);

  const caricaDati = async () => {
    try {
      setLoading(true);
      
      // I numeri arrivano dal server calcolati sull'intero archivio: le liste
      // sono paginate a 20 elementi, contarle qui darebbe risultati sbagliati.
      const statsRes = await api.get('/abbonamenti/report/dashboard/');
      const s = statsRes.data;

      // L'elenco serve solo per la tabella "ultimi abbonamenti"
      const abbRes = await api.get('/abbonamenti/abbonamenti/');
      const abbonamentiData = abbRes.data.results || abbRes.data;
      setAbbonamenti(abbonamentiData);

      // Carica abbonamenti in scadenza da cache Redis (14 giorni)
      try {
        const scadenzaRes = await api.get('/abbonamenti/abbonamenti/scadenza_cached/?periodo=14');
        setAbbonamentiInScadenza(scadenzaRes.data.abbonamenti || []);
        setScadenzaFromCache(scadenzaRes.data.from_cache || false);
        setScadenzaLastUpdate(scadenzaRes.data.last_update || null);
      } catch (error) {
        console.error('Errore caricamento abbonamenti in scadenza:', error);
        setAbbonamentiInScadenza([]);
      }

      const attivi = s.abbonamenti_attivi;
      const scaduti = s.abbonamenti_scaduti;
      const inAttesaPagamento = s.abbonamenti_in_attesa;

      setStats({
        clienti_totali: s.clienti_totali,
        abbonamenti_attivi: attivi,
        abbonamenti_scaduti: scaduti,
        pagamenti_sospeso: inAttesaPagamento,
        linee_attive: s.gruppi_attivi,
        corse_oggi: s.corse_oggi,
        per_gruppo: s.per_gruppo || []
      });

      if (s.controlli_oggi.totale > 0) {
        setControlliData([
          { nome: 'Validi', valore: s.controlli_oggi.validi, colore: '#1b94ce' },
          { nome: 'Respinti', valore: s.controlli_oggi.respinti, colore: '#e62494' }
        ]);
      }

      const avvisiReali = [];
      if (scaduti > 0) {
        avvisiReali.push({
          id: 1,
          testo: `${scaduti} Abbonamenti Scaduti da Rinnovare`,
          tipo: 'oggi',
          colore: 'bg-red-50 text-red-900 border border-red-200'
        });
      }
      if (inAttesaPagamento > 0) {
        avvisiReali.push({
          id: 2,
          testo: `${inAttesaPagamento} Pagamenti in Attesa`,
          tipo: 'verificare',
          colore: 'bg-orange-50 text-orange-900 border border-orange-200'
        });
      }
      setAvvisi(avvisiReali);

      // Anche l'andamento arriva già calcolato: contarlo qui significherebbe
      // basarsi sulla sola prima pagina di abbonamenti
      setAndamentoData(s.andamento || []);

    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b94ce]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Panoramica del sistema</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Clienti</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.clienti_totali}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-[#1b94ce] to-[#157bb0] rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Abbonamenti Attivi</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.abbonamenti_attivi}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-[#1b94ce] to-[#157bb0] rounded-lg flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Gruppi Attivi</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.linee_attive}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-[#e62494] to-[#c21e7d] rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Pagamenti Sospesi</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.pagamenti_sospeso}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-[#e62494] to-[#c21e7d] rounded-lg flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Grafici */}
        {/* Abbonamenti attivi per gruppo di linea */}
        {stats.per_gruppo?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            {stats.per_gruppo.map((g) => (
              <div key={g.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 uppercase truncate">
                      Abbonamenti attivi · {g.nome}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{g.attivi}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      su {g.totali} {g.totali === 1 ? 'abbonamento' : 'abbonamenti'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-gradient-to-br from-[#1b94ce] to-[#157bb0] rounded-lg flex items-center justify-center shrink-0">
                    <Bus className="w-4 h-4 text-white" />
                  </div>
                </div>

                {(g.in_attesa > 0 || g.scaduti > 0) && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                    {g.in_attesa > 0 && (
                      <span className="px-2 py-1 text-[11px] font-medium rounded-full bg-orange-50 text-orange-800 border border-orange-200">
                        {g.in_attesa} da saldare
                      </span>
                    )}
                    {g.scaduti > 0 && (
                      <span className="px-2 py-1 text-[11px] font-medium rounded-full bg-red-50 text-red-800 border border-red-200">
                        {g.scaduti} scaduti
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Andamento Abbonamenti</h3>
            {andamentoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={andamentoData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="mese" tick={{fontSize: 12}} />
                  <YAxis tick={{fontSize: 12}} />
                  <Tooltip contentStyle={{fontSize: 12, borderRadius: 8}} />
                  <Legend wrapperStyle={{fontSize: 12}} />
                  <Line type="monotone" dataKey="nuovi" stroke="#1b94ce" strokeWidth={2} name="Nuovi" />
                  <Line type="monotone" dataKey="scaduti" stroke="#e62494" strokeWidth={2} name="Scaduti" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-12 text-center text-gray-400">
                <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessun dato disponibile</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Controlli Oggi</h3>
            {controlliData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={controlliData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ nome, valore }) => `${nome}: ${valore}`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="valore"
                  >
                    {controlliData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.colore} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{fontSize: 12, borderRadius: 8}} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-12 text-center text-gray-400">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessun controllo</p>
              </div>
            )}
          </div>
        </div>

        {/* Abbonamenti in Scadenza - Card Dedicata */}
        <div className="mb-5">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                Abbonamenti in Scadenza (prossimi 14 giorni)
              </h3>
              <div className="flex items-center gap-2">
                {scadenzaFromCache && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Cache
                  </span>
                )}
                {abbonamentiInScadenza.length > 0 && (
                  <span className="px-3 py-1 bg-orange-600 text-white text-xs font-bold rounded-full">
                    {abbonamentiInScadenza.length}
                  </span>
                )}
              </div>
            </div>
            
            {scadenzaLastUpdate && (
              <p className="text-xs text-gray-500 mb-3">
                Ultimo aggiornamento: {new Date(scadenzaLastUpdate).toLocaleString('it-IT')}
              </p>
            )}
            
            {abbonamentiInScadenza.length > 0 ? (
              <>
                <div className="space-y-3">
                  {abbonamentiInScadenza.slice(0, 5).map((abb) => {
                    // I dati dalla cache hanno già giorni_rimanenti calcolati
                    const giorniRimanenti = abb.giorni_rimanenti !== undefined 
                      ? abb.giorni_rimanenti 
                      : Math.ceil((new Date(abb.data_fine) - new Date()) / (1000 * 60 * 60 * 24));
                    
                    const isUrgente = giorniRimanenti <= 3;
                    
                    return (
                      <div 
                        key={abb.id} 
                        className={`flex items-center justify-between p-4 rounded-lg transition hover:shadow-md ${
                          isUrgente 
                            ? 'bg-red-50 border-2 border-red-300' 
                            : 'bg-white border-2 border-orange-200'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-bold text-gray-900">
                              {abb.titolare_nome || 'Cliente'}
                            </p>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-mono rounded">
                              {abb.codice}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Scade il {new Date(abb.data_fine).toLocaleDateString('it-IT')}
                            </span>
                            <span className={`font-bold ${
                              isUrgente ? 'text-red-600' : 'text-orange-600'
                            }`}>
                              {giorniRimanenti === 0 ? 'SCADE OGGI' : 
                               giorniRimanenti === 1 ? 'Scade domani' :
                               `Tra ${giorniRimanenti} giorni`}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/abbonamenti/${abb.id}`)}
                          className={`px-4 py-2 text-sm font-semibold rounded-lg transition flex items-center gap-1.5 ${
                            isUrgente
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-orange-600 hover:bg-orange-700 text-white'
                          }`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Rinnova
                        </button>
                      </div>
                    );
                  })}
                </div>
                
                {abbonamentiInScadenza.length > 5 && (
                  <button
                    onClick={() => navigate('/admin/abbonamenti')}
                    className="mt-4 w-full text-center py-2.5 bg-white hover:bg-orange-50 border-2 border-orange-300 text-orange-700 font-semibold text-sm rounded-lg transition"
                  >
                    Vedi tutti i {abbonamentiInScadenza.length} abbonamenti in scadenza →
                  </button>
                )}
              </>
            ) : (
              <div className="py-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-40" />
                <p className="text-sm font-medium text-gray-600">
                  Ottimo! Nessun abbonamento in scadenza nei prossimi 14 giorni
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Avvisi e Abbonamenti */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#e62494]" />
              Avvisi
            </h3>
            {avvisi.length > 0 ? (
              <div className="space-y-2">
                {avvisi.map((avviso) => (
                  <div key={avviso.id} className={`${avviso.colore} rounded-lg p-3 flex items-center justify-between`}>
                    <div>
                      <p className="text-sm font-medium">{avviso.testo}</p>
                      <p className="text-xs mt-0.5 opacity-70">
                        {avviso.tipo === 'oggi' ? 'Da gestire oggi' : 'Da verificare'}
                      </p>
                    </div>
                    <button className="bg-gray-900 hover:bg-gray-800 text-white px-3 py-1.5 rounded-lg text-xs transition font-medium">
                      Gestisci
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-gray-400">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Tutto sotto controllo!</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#1b94ce]" />
              Ultimi Abbonamenti
            </h3>
            {abbonamenti.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-2 text-left text-xs font-semibold text-gray-700">Codice</th>
                      <th className="py-2 text-left text-xs font-semibold text-gray-700">Cliente</th>
                      <th className="py-2 text-left text-xs font-semibold text-gray-700">Scadenza</th>
                      <th className="py-2 text-left text-xs font-semibold text-gray-700">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abbonamenti.slice(0, 5).map((abb) => (
                      <tr key={abb.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 text-xs font-medium">{abb.codice}</td>
                        <td className="py-2 text-xs">{abb.titolare_nome || 'N/A'}</td>
                        <td className="py-2 text-xs">{new Date(abb.data_fine).toLocaleDateString('it-IT')}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            abb.stato === 'ATTIVO' ? 'bg-green-100 text-green-800' :
                            abb.stato === 'SCADUTO' ? 'bg-red-100 text-red-800' :
                            abb.stato === 'IN_ATTESA_PAGAMENTO' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {abb.stato.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center text-gray-400">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessun abbonamento</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default DashboardAdmin;
